import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { AuthService } from 'src/app/service/auth.service';
import { map } from 'rxjs/operators';

export interface ReportSubscription {
  uid?: string;
  schedule: string;
  displayable: string;
  active: boolean;
  score: string;
  recipients: string[];
}
interface ReportSubscriptionResponse {
  hits: ReportSubscription[];
}

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private readonly customerApi: string;

  private subscriptionsSubject = new BehaviorSubject<ReportSubscription[]>([]);
  public subscriptions$ = this.subscriptionsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    public routing: RoutingService,
    public config: ConfigService,
  ) {
    this.customerApi = this.config.appConfig.urlCustomerApi;
  }

  /**
   * Get all report subscriptions and update the BehaviorSubject
   * @returns An observable of ReportSubscription array
   */
  getAllSubscriptions(): Observable<ReportSubscription[]> {
    return this.http
      .get<ReportSubscriptionResponse>(
        this.customerApi + '/v1/reporting/subscriptions',
        {
          headers: this.auth.getHeader(),
        },
      )
      .pipe(
        map((response) => {
          this.subscriptionsSubject.next(response.hits); // Update the BehaviorSubject
          return response.hits;
        }),
      );
  }

  /**
   * Create a new report subscription or update an existing one if uid is provided
   * @param subscription The subscription data
   * @returns An observable of the created/updated ReportSubscription
   */
  saveSubscription(
    subscription: ReportSubscription,
  ): Observable<ReportSubscription> {
    return this.http.post<ReportSubscription>(
      this.customerApi + '/v1/reporting/subscription',
      subscription,
      {
        headers: this.auth.getHeader(),
      },
    );
  }

  /**
   * Create or update a new report subscription and refresh the subscription list
   * @param subscription The subscription data
   * @returns An observable of the created ReportSubscription
   */
  createOrUpdateSubscription(
    subscription: Partial<ReportSubscription>,
  ): Observable<ReportSubscription> {
    return this.saveSubscription(subscription as ReportSubscription).pipe(
      map(() => {
        // Fetch the updated list of subscriptions
        this.getAllSubscriptions().subscribe();
        return subscription as ReportSubscription;
      }),
    );
  }

  /**
   * Update an existing report subscription and refresh the subscription list
   * @param subscription The subscription data with uid
   * @returns An observable of the updated ReportSubscription
   */
  updateSubscriptionStatus(
    subscription: ReportSubscription,
  ): Observable<ReportSubscription> {
    if (!subscription.uid) {
      throw new Error('Subscription uid is required for updates');
    }
    return this.saveSubscription(subscription).pipe(
      map(() => {
        return subscription as ReportSubscription;
      }),
    );
  }

  /**
   * Delete a report subscription and refresh the subscription list
   * @param uid The unique identifier of the subscription to delete
   * @returns An observable of the delete operation result
   */
  deleteSubscription(uid: string): Observable<void> {
    const params = new HttpParams().set('uid', uid);
    return this.http
      .delete<void>(this.customerApi + '/v1/reporting/subscription', {
        params,
        headers: this.auth.getHeader(),
      })
      .pipe(
        map(() => {
          const currentSubscriptions = this.subscriptionsSubject.getValue();
          const updatedSubscriptions = currentSubscriptions.filter(
            (sub) => sub.uid !== uid,
          );
          this.subscriptionsSubject.next(updatedSubscriptions);
        }),
      );
  }
}
